import { useState } from "react";

const cleanFilters = <T extends Record<string, any>>(filters: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(filters).filter(([_, values]) => (Array.isArray(values) ? values.length > 0 : !!values)),
  ) as Partial<T>;
};

interface UseFilterSheetConfig<T> {
  initialFilters: T;
  onApply: (filters: T, cleanedFilters: Partial<T>) => void;
  getActiveCount: (filters: T) => number;
}

export function useFilterSheet<T extends Record<string, any>>({
  initialFilters,
  onApply,
  getActiveCount,
}: UseFilterSheetConfig<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<T>(initialFilters);

  const hasLocalFilters = Object.values(localFilters).some((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)));

  return {
    filterButton: {
      onClick: () => {
        setLocalFilters(initialFilters);
        setIsOpen(true);
      },
      activeCount: getActiveCount(initialFilters),
    },
    filterSheetProps: {
      isOpen,
      onOpenChange: setIsOpen,
      onApply: () => {
        const cleaned = cleanFilters(localFilters);
        onApply(localFilters, cleaned);
        setIsOpen(false);
      },
      onCancel: () => {
        setLocalFilters(initialFilters);
        setIsOpen(false);
      },
      onClear: () => {
        const cleared = Object.keys(localFilters).reduce(
          (acc, key) => ({
            ...acc,
            [key]: undefined,
          }),
          {} as T,
        );
        setLocalFilters(cleared);
        const cleaned = cleanFilters(cleared);
        onApply(cleared, cleaned);
        setIsOpen(false);
      },
      hasFilters: hasLocalFilters,
    },
    localFilters,
    setLocalFilters,
  };
}

export type UseFilterSheetProps = ReturnType<typeof useFilterSheet>;
export type FilterButtonProps = UseFilterSheetProps["filterButton"];
